/**
 * AntragVorbereitenDialog - «Antrag vorbereiten» / «Antrag aktualisieren» (Tab 5a, P2 Slice 13).
 * Shows the Kundenentscheid summary + the form source, then Beginn (required) / Jahrespraemie /
 * Bemerkung. The server owns the gates; this only collects the body.
 */

import { useState } from 'react';
import {
  ANTRAG_KEIN_FORMULAR_HINWEIS,
  ANTRAG_OFFEN_HINWEIS,
  ANTRAG_KEIN_ENTSCHEID_HINWEIS,
  type AntragResponse,
  type AntragVorbereitenBody,
} from '@brokly/shared';
import { dateShortDe } from '../../lib/format';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';

const ERROR_TEXT: Record<string, string> = {
  begin_date_required: 'Bitte Versicherungsbeginn angeben',
  template_unavailable: 'Das Versicherer-Formular ist nicht verfuegbar - bitte in den Versicherer-Einstellungen pruefen',
  render_failed: 'Der Antrag konnte nicht erstellt werden',
  antrag_offen: ANTRAG_OFFEN_HINWEIS,
  kein_kundenentscheid: ANTRAG_KEIN_ENTSCHEID_HINWEIS,
};

export function AntragVorbereitenDialog({
  open,
  onClose,
  facts,
  onVorbereiten,
  saving,
  error,
  mode,
}: {
  open: boolean;
  onClose: () => void;
  facts: AntragResponse;
  onVorbereiten: (body: AntragVorbereitenBody) => void;
  saving: boolean;
  error: { code?: string } | null;
  mode: 'neu' | 'bearbeiten';
}) {
  const [beginDate, setBeginDate] = useState(facts.antrag?.begin_date ?? facts.begin_date_vorschlag ?? '');
  const [praemie, setPraemie] = useState<string>(
    String(facts.antrag?.praemie_jahr ?? facts.praemie_jahr_vorschlag ?? ''),
  );
  const [bemerkung, setBemerkung] = useState(facts.antrag?.bemerkung ?? '');

  const errorText = error?.code ? (ERROR_TEXT[error.code] ?? 'Antrag konnte nicht vorbereitet werden') : null;
  const templateVorhanden = facts.formular.template_vorhanden;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={mode === 'bearbeiten' ? 'Antrag bearbeiten' : 'Antrag vorbereiten'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button
            disabled={saving || beginDate.trim() === ''}
            onClick={() =>
              onVorbereiten({
                begin_date: beginDate || undefined,
                bemerkung: bemerkung.trim() ? bemerkung.trim() : null,
                praemie_jahr: praemie.trim() ? Number(praemie) : null,
              })
            }
            data-testid="antrag-vorbereiten-submit"
          >
            {mode === 'bearbeiten' ? 'Antrag aktualisieren' : 'Antrag vorbereiten'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm" data-testid="antrag-vorbereiten-dialog">
        {facts.entscheid && (
          <p className="text-muted-foreground">
            Kundenentscheid: {facts.entscheid.carrier_name ?? facts.entscheid.carrier_uid} ({facts.entscheid.submission_number}) vom{' '}
            {dateShortDe(facts.entscheid.decided_at)}
          </p>
        )}
        <p>
          Formular:{' '}
          {facts.formular.form_template_ref && templateVorhanden
            ? `Versicherer-Formular ${facts.formular.template_name ?? ''}`
            : 'Brokly-Antrag'}
        </p>
        {facts.formular.form_template_ref && !templateVorhanden && (
          <p className="text-xs text-muted-foreground">{ANTRAG_KEIN_FORMULAR_HINWEIS}</p>
        )}
        <label className="block">
          <span className="text-xs text-muted-foreground">Versicherungsbeginn</span>
          <input
            type="date"
            value={beginDate}
            onChange={(e) => setBeginDate(e.target.value)}
            className="mt-1 w-full rounded-md border border-border p-2"
            data-testid="antrag-begin-date"
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Jahrespraemie (CHF)</span>
          <input
            type="number"
            min={0}
            value={praemie}
            onChange={(e) => setPraemie(e.target.value)}
            className="mt-1 w-full rounded-md border border-border p-2"
            data-testid="antrag-praemie"
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Bemerkung auf dem Antrag</span>
          <textarea
            maxLength={2000}
            value={bemerkung}
            onChange={(e) => setBemerkung(e.target.value)}
            className="mt-1 w-full rounded-md border border-border p-2"
            data-testid="antrag-bemerkung"
          />
        </label>
        {errorText && <p className="text-sm text-status-danger" data-testid="antrag-vorbereiten-error">{errorText}</p>}
      </div>
    </Dialog>
  );
}
